import * as React from "react";
import Axios from "axios";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Cookies from "js-cookie";
import Title from "@/components/molecules/Title";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import toast from "@/components/hoc/SnackbarAlert";
import { Office, ONLY_CONTRACT } from "@/models/office";
import { getOfficesAPI } from "@/util/api/API";
import { getBillingPaymentScheduleAPI } from "@/util/api/billingPaymentSchedule";
import { BillingPaymentScheduleCalendar } from "@/models/billingPaymentSchedule";
import { CommonConstants, SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import { addMonths, splitYearMonth } from "@/util/datetime";
import {
  BillingPaymentScheduleConstants,
  getCurrentYearMonth,
  MIN_YEAR_MONTH,
} from "./BillingPaymentScheduleConstants";
import { BillingPaymentScheduleMonthCard } from "./BillingPaymentScheduleMonthCard";
import { BillingPaymentScheduleLegend } from "./BillingPaymentScheduleLegend";
import {
  buildOverflowNotices,
  buildScheduleEvents,
  filterEventsForYearMonth,
  filterEventsOutsideYearMonth,
} from "./BillingPaymentScheduleEvents";

// httpClientのレスポンスインターセプタが404/409以外は既にtoast.errorを表示するため、
// ここで重ねて表示すると二重通知になる。404/409のときだけ自前で表示する。
const needsOwnToast = (e: unknown) =>
  !Axios.isAxiosError(e) || e.response?.status === 404 || e.response?.status === 409;

function BillingPaymentScheduleContent(): JSX.Element {
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office | null>(
    null
  );
  const [baseYearMonth, setBaseYearMonth] = React.useState(
    getCurrentYearMonth()
  );
  // 前月分は自月のカードとしては表示しないが、口座振替引落日等が営業日調整により
  // 表示月へ繰り越すケースを拾うためにイベント化のみ行う（1637-Copilotレビュー指摘対応）。
  const [prevMonthData, setPrevMonthData] =
    React.useState<BillingPaymentScheduleCalendar | null>(null);
  const [currentMonthData, setCurrentMonthData] =
    React.useState<BillingPaymentScheduleCalendar | null>(null);
  const [nextMonthData, setNextMonthData] =
    React.useState<BillingPaymentScheduleCalendar | null>(null);

  // 営業所切り替え・前月/翌月切り替えを短時間に連続して行った場合、古いリクエストの
  // 応答が新しいリクエストより後に返ることがある。この世代カウンタで最新のリクエスト
  // だけが状態を更新できるようにする。
  const requestIdRef = React.useRef(0);

  const fetchSchedule = React.useCallback(
    (office: Office, yearMonth: string) => {
      const requestId = ++requestIdRef.current;
      const prevYearMonth = addMonths(yearMonth, -1);
      const nextYearMonth = addMonths(yearMonth, 1);
      const { year, month } = splitYearMonth(yearMonth);
      const { year: prevYear, month: prevMonth } = splitYearMonth(
        prevYearMonth
      );
      const { year: nextYear, month: nextMonth } = splitYearMonth(
        nextYearMonth
      );

      // 選択中の営業所・年月と紐づかない古い応答で新しい選択の表示を上書きしないよう、
      // 取得中は一旦クリアする。
      setPrevMonthData(null);
      setCurrentMonthData(null);
      setNextMonthData(null);

      getBillingPaymentScheduleAPI(office.id, prevYear, prevMonth)
        .then(({ data }) => {
          if (requestIdRef.current !== requestId) return;
          setPrevMonthData(data);
        })
        .catch((e) => {
          console.log(
            "===Error occurred while getting billing payment schedule===",
            e
          );
          if (requestIdRef.current !== requestId) return;
          if (needsOwnToast(e)) {
            toast.error(BillingPaymentScheduleConstants.FetchError);
          }
          setPrevMonthData(null);
        });

      getBillingPaymentScheduleAPI(office.id, year, month)
        .then(({ data }) => {
          if (requestIdRef.current !== requestId) return;
          setCurrentMonthData(data);
        })
        .catch((e) => {
          console.log(
            "===Error occurred while getting billing payment schedule===",
            e
          );
          if (requestIdRef.current !== requestId) return;
          if (needsOwnToast(e)) {
            toast.error(BillingPaymentScheduleConstants.FetchError);
          }
          setCurrentMonthData(null);
        });

      getBillingPaymentScheduleAPI(office.id, nextYear, nextMonth)
        .then(({ data }) => {
          if (requestIdRef.current !== requestId) return;
          setNextMonthData(data);
        })
        .catch((e) => {
          console.log(
            "===Error occurred while getting billing payment schedule===",
            e
          );
          if (requestIdRef.current !== requestId) return;
          if (needsOwnToast(e)) {
            toast.error(BillingPaymentScheduleConstants.FetchError);
          }
          setNextMonthData(null);
        });
    },
    []
  );

  const handleChangeOffice = React.useCallback(
    (_e: React.SyntheticEvent<Element, Event>, value: Office | null) => {
      setOfficeSelected(value);
      Cookies.set(SINGLE_OFFICE_ID, value?.id.toString() ?? "");
      if (value) fetchSchedule(value, baseYearMonth);
    },
    [baseYearMonth, fetchSchedule]
  );

  // 2025年以前のカレンダーは表示させないため、表示可能な最も古い年月(MIN_YEAR_MONTH)に
  // 達している場合は「前月」ボタンを非活性にする。
  const isPrevMonthDisabled = baseYearMonth <= MIN_YEAR_MONTH;

  const handlePrevMonth = React.useCallback(() => {
    if (isPrevMonthDisabled) return;
    const prev = addMonths(baseYearMonth, -1);
    setBaseYearMonth(prev);
    if (officeSelected) fetchSchedule(officeSelected, prev);
  }, [baseYearMonth, isPrevMonthDisabled, officeSelected, fetchSchedule]);

  const handleNextMonth = React.useCallback(() => {
    const next = addMonths(baseYearMonth, 1);
    setBaseYearMonth(next);
    if (officeSelected) fetchSchedule(officeSelected, next);
  }, [baseYearMonth, officeSelected, fetchSchedule]);

  React.useEffect(() => {
    getOfficesAPI(ONLY_CONTRACT)
      .then(({ data }) => {
        const ofs = data ?? [];
        setOffices(ofs);

        const savedOffice = ofs.find(
          ({ id }) => id === Number(Cookies.get(SINGLE_OFFICE_ID))
        );
        const defaultOffice =
          savedOffice ??
          (ofs.length > 0
            ? ofs.reduce((min, o) => (o.id < min.id ? o : min))
            : null);

        setOfficeSelected(defaultOffice ?? null);
        if (defaultOffice) fetchSchedule(defaultOffice, getCurrentYearMonth());
      })
      .catch((e) =>
        console.log("===Error occurred while getting offices===", e)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextYearMonth = addMonths(baseYearMonth, 1);

  // 口座振替の引落日は「月末日（非営業日の場合は翌営業日）」というルール上、月末が
  // 土日の場合に実日付が翌月へ繰り越すことがある。そのため、表示中の月とその前後1ヶ月分の
  // APIレスポンスをまとめてイベント化し、各カードには実日付が属する月のイベントだけを
  // 振り分けて渡す（APIを呼び出した対象月と、イベントの実日付が属する月は必ずしも一致しない）。
  // 前月分は自月のカードとして表示しないが、前月の月末が繰り越して表示月の1日等になる
  // ケースを拾うためにイベント化のみ対象に含める。
  const allEvents = React.useMemo(() => {
    return [
      ...(prevMonthData ? buildScheduleEvents(prevMonthData) : []),
      ...(currentMonthData ? buildScheduleEvents(currentMonthData) : []),
      ...(nextMonthData ? buildScheduleEvents(nextMonthData) : []),
    ];
  }, [prevMonthData, currentMonthData, nextMonthData]);

  const currentMonthEvents = React.useMemo(
    () => filterEventsForYearMonth(allEvents, baseYearMonth),
    [allEvents, baseYearMonth]
  );
  const nextMonthEvents = React.useMemo(
    () => filterEventsForYearMonth(allEvents, nextYearMonth),
    [allEvents, nextYearMonth]
  );
  const currentMonthHolidays = React.useMemo(
    () => currentMonthData?.holidays ?? [],
    [currentMonthData]
  );
  const nextMonthHolidays = React.useMemo(
    () => nextMonthData?.holidays ?? [],
    [nextMonthData]
  );

  // 各カードが表示する月自身のAPIレスポンスから直接組み立てたイベント（他月のデータと
  // 混ざっていない、その月自身の請求・支払データのみ）。繰り越し判定の起点として使う。
  const currentMonthOwnEvents = React.useMemo(
    () => (currentMonthData ? buildScheduleEvents(currentMonthData) : []),
    [currentMonthData]
  );
  const nextMonthOwnEvents = React.useMemo(
    () => (nextMonthData ? buildScheduleEvents(nextMonthData) : []),
    [nextMonthData]
  );

  // 表示中の月（左カード）自身の日付が非営業日調整で翌月へ繰り越した場合、翌月が未公開だと
  // 翌月のカードに表示できない（準備中表示になるため）。表示中の月のカード下部に
  // カレンダー外の注釈として表示する。翌月が公開済みの場合は翌月のカード上に
  // 通常どおり表示されるため、注釈は表示しない。
  const currentMonthOverflowNotices = React.useMemo(
    () =>
      nextMonthData && !nextMonthData.published
        ? buildOverflowNotices(
            filterEventsOutsideYearMonth(currentMonthOwnEvents, baseYearMonth)
          )
        : [],
    [nextMonthData, currentMonthOwnEvents, baseYearMonth]
  );

  // 翌月（右カード）自身の日付が非営業日調整でさらに翌々月へ繰り越した場合、翌々月は
  // この2ヶ月表示には含まれず、他のどのカードにも表示されない。そのため公開状態に
  // かかわらず、常に翌月のカード下部にカレンダー外の注釈として表示する。
  const nextMonthOverflowNotices = React.useMemo(
    () =>
      buildOverflowNotices(
        filterEventsOutsideYearMonth(nextMonthOwnEvents, nextYearMonth)
      ),
    [nextMonthOwnEvents, nextYearMonth]
  );

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {BillingPaymentScheduleConstants.Title}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <AutoCompleteSearch
                    options={offices}
                    value={officeSelected}
                    onChange={handleChangeOffice}
                    getOptionLabel={getOfficeOptionLabel}
                    label={CommonConstants.CompanyOffice}
                  />
                </Grid>
                <Grid item xs container justifyContent="flex-end" spacing={1}>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handlePrevMonth}
                      disabled={isPrevMonthDisabled}
                    >
                      {BillingPaymentScheduleConstants.PrevMonth}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNextMonth}
                    >
                      {BillingPaymentScheduleConstants.NextMonth}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <BillingPaymentScheduleMonthCard
              yearMonth={baseYearMonth}
              published={currentMonthData?.published ?? false}
              events={currentMonthEvents}
              holidays={currentMonthHolidays}
              overflowNotices={currentMonthOverflowNotices}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <BillingPaymentScheduleMonthCard
              yearMonth={nextYearMonth}
              published={nextMonthData?.published ?? false}
              events={nextMonthEvents}
              holidays={nextMonthHolidays}
              overflowNotices={nextMonthOverflowNotices}
            />
          </Grid>
          <Grid item xs={12}>
            <BillingPaymentScheduleLegend
              settlementType={
                currentMonthData?.settlementType ??
                nextMonthData?.settlementType ??
                null
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export function BillingPaymentSchedule(): JSX.Element {
  return <BillingPaymentScheduleContent />;
}
